import {
  useInfiniteGetActiveLoansByMaturityQuery,
  type GetActiveLoansByMaturityQuery,
} from "~/types/graphql";
import { useMemo } from "react";

// Separate loan filtering logic
const isLoanDefaulted = (
  loan: GetActiveLoansByMaturityQuery["coolerLoans"][number]
) => loan.defaultedClaimEvents.length > 0;

const isLoanRepaid = (
  loan: GetActiveLoansByMaturityQuery["coolerLoans"][number]
) => loan.principal === "0";

const isLoanExpired = (
  loan: GetActiveLoansByMaturityQuery["coolerLoans"][number],
  currentTime: number
) => {
  const expired = loan.currentExpiryTimestamp <= currentTime;
  const repaid = loan.principal === "0";
  const defaulted = loan.defaultedClaimEvents.length > 0;
  return expired && !repaid && !defaulted;
};

interface UseLoanDataOptions {
  includeExpired?: boolean;
  date?: string;
}

export function useLoanData(options: UseLoanDataOptions = {}) {
  const now = Math.floor(Date.now() / 1000);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteGetActiveLoansByMaturityQuery(
      {
        first: 1000,
        // Pass date only if we want to filter by it
        ...(options.date || !options.includeExpired
          ? { date: options.date || now.toString() }
          : {}),
        skip: 0,
      },
      {
        getNextPageParam: (lastPage, allPages) => {
          // If we got less than the requested amount, there are no more pages
          if (lastPage.coolerLoans.length < 1000) {
            return undefined;
          }
          // Return the skip value for the next page
          return { skip: allPages.length * 1000 };
        },
        initialPageParam: { skip: 0 },
      }
    );

  // Combine all pages of loans into a single array using useMemo
  const allLoans = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.coolerLoans);
  }, [data?.pages]);

  // Filter loans into different categories using useMemo
  const { activeLoans, defaultedLoans, expiredLoans, repaidLoans } =
    useMemo(() => {
      const currentTime = options.date ? parseInt(options.date) : now;
      return {
        // If we're not filtering at query level, we need to filter here
        activeLoans: allLoans.filter(
          (loan) =>
            !isLoanRepaid(loan) &&
            (!options.includeExpired
              ? true
              : !isLoanExpired(loan, currentTime)) &&
            !isLoanDefaulted(loan)
        ),
        defaultedLoans: allLoans.filter(isLoanDefaulted),
        expiredLoans: allLoans.filter((loan) =>
          isLoanExpired(loan, currentTime)
        ),
        repaidLoans: allLoans.filter(isLoanRepaid),
      };
    }, [allLoans, now, options.includeExpired, options.date]);

  return {
    loans: allLoans,
    activeLoans,
    defaultedLoans,
    expiredLoans,
    repaidLoans,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}
